import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from './client'

export interface PersonaSummary {
  role: string
  status: 'Draft' | 'Published' | 'Reload-Rejected'
  lastModifiedBy: string
  etag: string
  frontMatter: any
}

export interface PersonaDetail {
  role: string
  frontMatter: any
  body: string
  status: string
  lastModifiedBy: string
}

export function usePersonasList() {
  return useQuery({
    queryKey: ['personas'],
    queryFn: async () => (await apiFetch<{ items: PersonaSummary[] }>('/api/personas')).data,
  })
}

interface PersonaDetailResult { data: PersonaDetail; etag?: string }

export function usePersonaDetail(role: string | null) {
  return useQuery({
    queryKey: ['persona', role],
    enabled: !!role,
    queryFn: async () => {
      const r = await apiFetch<PersonaDetail>(`/api/personas/${role}`)
      return { data: r.data, etag: r.etag } as PersonaDetailResult
    },
  })
}

export function useSaveDraft(role: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ frontMatter, body, etag }: { frontMatter: any; body: string; etag?: string }) => {
      const res = await apiFetch<{ etag: string }>(`/api/personas/${role}/draft`, {
        method: 'PUT',
        body: JSON.stringify({ frontMatter, body }),
        headers: etag ? { 'If-Match': etag } : undefined,
      })
      return res.data
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['personas'] }); qc.invalidateQueries({ queryKey: ['persona', role] }) },
  })
}

export interface VersionItem {
  v: number
  id: string
  kind: string
  sha: string
  at: string
  by: string
  current: boolean
  effectiveSha?: string | null
  usesFragments?: string[] | null
  fragmentDriftSince?: string | null
}

// Spec 350 — persona version-history timeline, incl. the effective (expanded) prompt hash + fragment drift.
export function useVersions(role: string | null) {
  return useQuery({
    queryKey: ['versions', role],
    enabled: !!role,
    queryFn: async () =>
      (await apiFetch<{ items: VersionItem[] }>(`/api/personas/${role}/versions`)).data.items,
  })
}

export function usePublishPersona(role: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ justification, etag }: { justification: string; etag?: string }) => {
      const res = await apiFetch<{ publishedSha: string; version: number; bridge: { reloaded: boolean } }>(
        `/api/personas/${role}/publish`,
        { method: 'POST', body: JSON.stringify({ justification, etag }) },
      )
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['personas'] }),
  })
}

export interface EffectiveAccess {
  upn: string
  oid: string
  effectiveRole: string
  effectiveRoles: string[]
  aclScope: string[] | null
  capabilities: string[]
}

export function useEffectiveAccess() {
  return useQuery({
    queryKey: ['access', 'me'],
    queryFn: async () => (await apiFetch<EffectiveAccess>('/api/access/me')).data,
    staleTime: 60_000,
  })
}

export function useValidatorRules() {
  return useQuery({
    queryKey: ['validate', 'rules'],
    queryFn: async () => (await apiFetch<any>('/api/validate')).data,
    staleTime: Infinity,
  })
}

export interface ToolEntry {
  id: string
  provider: string
  scope: 'read-only' | 'write-capable' | 'execute'
  description: string
  constraints: string | null
  classes: string[]
  notes: string | null
}

export function useToolCatalog() {
  return useQuery({
    queryKey: ['tools'],
    queryFn: async () => (await apiFetch<{ version: number; tools: ToolEntry[] }>('/api/tools')).data,
    staleTime: Infinity,
  })
}

export interface AuditItem {
  ts: string
  id: string
  kind: string
  actor: string
  ticket: string | null
  payload: unknown
  source: 'ui' | 'bridge'
}

export function useAuditLog(filters: { actor?: string; persona?: string; kind?: string } = {}) {
  const qs = new URLSearchParams()
  if (filters.actor) qs.set('actor', filters.actor)
  if (filters.persona) qs.set('persona', filters.persona)
  if (filters.kind) qs.set('kind', filters.kind)
  const suffix = qs.toString() ? `?${qs}` : ''
  return useQuery({
    queryKey: ['audit', filters],
    queryFn: async () => (await apiFetch<{ items: AuditItem[]; total: number; gaps: any[] }>(`/api/audit${suffix}`)).data,
  })
}

export interface GroupMapping {
  id: string
  groupOid: string
  groupDisplayName: string
  role: string
  scope: string[] | null
  createdBy: string
  createdAt: string
  justification: string
}

export function useGroupMappings() {
  return useQuery({
    queryKey: ['access', 'groups'],
    queryFn: async () => (await apiFetch<{ items: GroupMapping[] }>('/api/access/groups')).data,
  })
}

export function useCreateGroupMapping() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { groupOid: string; groupDisplayName?: string; role: string; scope?: string[] | null; justification: string }) => {
      const res = await apiFetch<{ id: string }>(`/api/access/groups`, {
        method: 'POST', body: JSON.stringify(payload),
      })
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['access', 'groups'] }),
  })
}

export function useTestAsUser() {
  return useMutation({
    mutationFn: async (upn: string) => {
      const res = await apiFetch<EffectiveAccess>(`/api/access/test-as-user`, {
        method: 'POST', body: JSON.stringify({ upn }),
      })
      return res.data
    },
  })
}

// Spec 351 — support→dev escalation mapping (the "Escalation" admin page). Maps each support project to its
// development project + a per-project escalation-enabled gate. The bridge reads this live.
export interface SupportDevMapping {
  supportProjectKey: string
  devProjectKey: string
  escalationEnabled: boolean
  // 357 — per-project gate for the client-facing "assigned to Development" note; independent of escalation.
  clientNoteEnabled: boolean
  updatedAt: string | null
  updatedBy: string | null
}

export function useSupportDevMappings() {
  return useQuery({
    queryKey: ['support-dev-mappings'],
    queryFn: async () => (await apiFetch<{ mappings: SupportDevMapping[] }>('/api/support-dev-mappings')).data.mappings,
  })
}

export function useUpsertSupportDevMapping() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: { supportProjectKey: string; devProjectKey: string; escalationEnabled: boolean; clientNoteEnabled: boolean }) => {
      const res = await apiFetch<{ supportProjectKey: string; created: boolean }>(`/api/support-dev-mappings`, {
        method: 'PUT', body: JSON.stringify(payload),
      })
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['support-dev-mappings'] }),
  })
}
